import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Panel } from '../../ui/index.ts';
import { Banknotes } from '../fx/Banknotes.tsx';
import { HideoutLamp } from '../fx/HideoutLamp.tsx';
import { SuspectBoard } from '../fx/SuspectBoard.tsx';
import { useGame } from '../GameContext.tsx';
import { EventFeed } from '../hud/EventFeed.tsx';
import { PhrasePad } from '../hud/PhrasePad.tsx';
import { TeamList } from '../hud/TeamList.tsx';
import styles from './Hideout.module.css';

/** The hideout (GDD §5.6): discuss and pin, vote in secret, split the loot. */
export function Hideout() {
  const { t } = useTranslation('game');
  const { view } = useGame();
  const h = view.hideout;
  if (!h) return null;
  return (
    <div className={styles['layout']}>
      <div className={styles['main']}>
        <HideoutLamp />
        <Panel tone="lamp" title={t(`hideout.stage.${h.stage}`)}>
          <p>{t(`hideout.intro.${h.stage}`)}</p>
        </Panel>
        {h.stage === 'discussion' ? <Discussion /> : null}
        {h.stage === 'accusation' ? <Accusation /> : null}
        {h.stage === 'split' ? <Split /> : null}
      </div>
      <aside className={styles['side']}>
        <Panel tone="night">
          <TeamList compact />
        </Panel>
        <EventFeed limit={8} />
        <PhrasePad />
      </aside>
    </div>
  );
}

function others(view: ReturnType<typeof useGame>['view']) {
  return view.players.filter((p) => p.id !== view.you.id && !p.expelled);
}

function Discussion() {
  const { t } = useTranslation('game');
  const { view, send } = useGame();
  const mine = view.players.find((p) => p.id === view.you.id)?.pin ?? null;
  const alive = view.players.filter((p) => !p.expelled);
  return (
    <Panel tone="night" title={t('hideout.board')}>
      <SuspectBoard
        suspects={alive.map((p) => ({
          id: p.id,
          name: p.name,
          role: p.role,
          seat: p.seat,
          pin: p.pin,
          mine: p.id === view.you.id,
        }))}
        mine={mine}
        onPin={(target) => send({ type: 'pin.place', target })}
      />
    </Panel>
  );
}

function Accusation() {
  const { t } = useTranslation('game');
  const { view, send } = useGame();
  const h = view.hideout;
  if (!h) return null;
  const voted = h.myAccusation !== null;
  const me = view.players.find((p) => p.id === view.you.id);
  return (
    <Panel tone="night" title={t('hideout.accuse')}>
      {me?.expelled ? (
        <p>{t('hideout.out')}</p>
      ) : voted ? (
        <p role="status">{t('hideout.voteCast')}</p>
      ) : (
        <div className={styles['choices']}>
          {others(view).map((p) => (
            <Button
              key={p.id}
              variant="danger"
              onClick={() => send({ type: 'accuse.vote', target: p.id })}
            >
              {t('hideout.accusePlayer', { name: p.name })}
            </Button>
          ))}
          <Button variant="secondary" onClick={() => send({ type: 'accuse.vote', target: 'none' })}>
            {t('hideout.nobody')}
          </Button>
        </div>
      )}
      <p className={styles['small']}>
        {t('hideout.voted', { count: h.voted.length, total: view.players.length })}
      </p>
    </Panel>
  );
}

function Split() {
  const { t } = useTranslation('game');
  const { view, send } = useGame();
  const h = view.hideout;
  const alive = view.players.filter((p) => !p.expelled);
  const equal = Math.floor(100 / Math.max(1, alive.length));
  const [shares, setShares] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      alive.map((p, i) => [p.id, i === 0 ? 100 - equal * (alive.length - 1) : equal]),
    ),
  );
  if (!h) return null;
  const total = Object.values(shares).reduce((a, b) => a + b, 0);
  const proposer = view.players.find((p) => p.id === h.proposer);
  const iPropose = h.proposer === view.you.id && !h.splitVoting;
  const me = view.players.find((p) => p.id === view.you.id);

  if (iPropose) {
    return (
      <Panel tone="night" title={t('hideout.splitYours')} className={styles['money']}>
        <Banknotes count={9} />
        <ul className={styles['shares']}>
          {alive.map((p) => (
            <li key={p.id}>
              <label>
                <span>{p.name}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={shares[p.id] ?? 0}
                  onChange={(e) =>
                    setShares((s) => ({
                      ...s,
                      [p.id]: Math.min(100, Math.max(0, Math.round(Number(e.target.value) || 0))),
                    }))
                  }
                />
                <span>{'%'}</span>
              </label>
            </li>
          ))}
        </ul>
        <p className={total === 100 ? styles['ok'] : styles['bad']} role="status">
          {t('hideout.total', { total })}
        </p>
        <div className={styles['choices']}>
          <Button disabled={total !== 100} onClick={() => send({ type: 'split.propose', shares })}>
            {t('hideout.propose')}
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              setShares(
                Object.fromEntries(
                  alive.map((p, i) => [p.id, i === 0 ? 100 - equal * (alive.length - 1) : equal]),
                ),
              )
            }
          >
            {t('hideout.equalSplit')}
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel tone="night" title={t('hideout.splitTitle')} className={styles['money']}>
      <Banknotes count={9} />
      <p>{t('hideout.proposer', { name: proposer?.name ?? '?' })}</p>
      {h.splitRejections > 0 ? (
        <p className={styles['small']}>{t('hideout.rejections', { count: h.splitRejections })}</p>
      ) : null}
      {h.proposal ? (
        <>
          <ul className={styles['shares']}>
            {alive.map((p) => (
              <li key={p.id}>
                <span>{p.name}</span> <strong>{`${h.proposal?.[p.id] ?? 0}%`}</strong>
              </li>
            ))}
          </ul>
          {h.splitVoting && h.mySplitVote === null && !me?.expelled ? (
            <div className={styles['choices']}>
              <Button icon="check" onClick={() => send({ type: 'split.vote', approve: true })}>
                {t('hideout.accept')}
              </Button>
              <Button
                variant="danger"
                icon="close"
                onClick={() => send({ type: 'split.vote', approve: false })}
              >
                {t('hideout.refuse')}
              </Button>
            </div>
          ) : (
            <p role="status">{t('hideout.waitingVotes')}</p>
          )}
        </>
      ) : (
        <p role="status">{t('hideout.waitingProposal')}</p>
      )}
    </Panel>
  );
}
